import { useEffect, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { WsClient, type ServerEventType, type ServerEvents } from './ws';
import { endpoints } from '@/api';
import { ApiError } from '@/api/errors';
import { qk, newerDate, newerGame } from '@/api/queries';
import type { ChatSummary, Connection, DateInvite, DateView, FriendRequest, GameView, Invite, Message, Pod, Proposal, Session, Wallet } from '@/api/types';
import { serverClock } from '@/lib/clock';
import { normalizeDate, normalizeGame, normalizeMessage } from '@/api/normalize';
import { applyEffect } from '@/features/games/effects';
import { useRealtime } from '@/stores/realtime';
import { useModeration } from '@/stores/moderation';
import { useAuth } from '@/stores/auth';
import { useChat } from '@/stores/chat';
import { wireUs } from '@/features/us/state';

export const realtime = new WsClient({
  getTicket: async () => (await endpoints.wsTicket()).ticket,
  isFatal: (e) => e instanceof ApiError && (e.status === 401 || e.code === 'account_suspended'),
});

realtime.onStatus((s) => useRealtime.getState().setStatus(s));

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => realtime.wake());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') realtime.wake();
  });
}

export interface BridgeCallbacks {
  onMatchFound?: (s: Session) => void;
  onInviteReceived?: (i: Invite) => void;
  onConnectionCreated?: (c: Connection) => void;
  onFriendRequest?: (r: FriendRequest) => void;
  onChatMessage?: (m: Message, friendName: string | null) => void;
  onPlanUpdated?: (plan: 'free' | 'plus') => void;
  /** v6.1: "Your date with Maya starts in 15 min" (once per scheduled date). */
  onDateReminder?: (invite: DateInvite) => void;
}

export const TYPING_SHOW_MS = 4000;

type Requests = { incoming: FriendRequest[]; outgoing: FriendRequest[] };
type Chats = { chats: ChatSummary[] };

/** Fold a message into the chats list: last message, unread count, re-sort by activity. */
export function applyChatMessage(prev: Chats | undefined, m: Message, myId: string | undefined, activeId: string | null): Chats | undefined {
  if (!prev) return prev;
  const idx = prev.chats.findIndex((c) => c.connection_id === m.connection_id);
  if (idx === -1) return prev;
  const c = prev.chats[idx]!;
  const isNew = !c.last_message || m.id > c.last_message.id;
  const fromFriend = m.sender_id !== myId;
  const next: ChatSummary = {
    ...c,
    last_message: isNew || c.last_message?.id === m.id ? m : c.last_message,
    unread_count: isNew && fromFriend && !m.deleted && activeId !== m.connection_id ? c.unread_count + 1 : c.unread_count,
  };
  const rest = prev.chats.filter((_, i) => i !== idx);
  return { chats: isNew ? [next, ...rest] : prev.chats.map((x, i) => (i === idx ? next : x)) };
}

/**
 * Wire server events into the TanStack Query cache + Zustand stores.
 * Pure wiring — UI reactions (navigation, toasts) come in through callbacks.
 */
export function wireRealtime(ws: WsClient, qc: QueryClient, cb: BridgeCallbacks = {}): () => void {
  const offs: (() => void)[] = [];
  const on = <K extends ServerEventType>(t: K, h: (d: ServerEvents[K]) => void) => offs.push(ws.on(t, h));

  on('hello', (d) => d?.server_time && serverClock.sample(d.server_time));

  // Back after a drop (a half-open socket, a phone that slept): events sent
  // meanwhile are never replayed (Doodle strokes, a round ending, the next
  // date card, the call's state), so read the live ones again.
  let wasUp = ws.status === 'connected';
  offs.push(
    ws.onStatus((s) => {
      if (s !== 'connected') return;
      if (wasUp) for (const key of [qk.game(''), qk.date(''), qk.session('')]) void qc.invalidateQueries({ queryKey: [key[0]] });
      wasUp = true;
    }),
  );
  on('pong', (d) => d?.server_time && serverClock.sample(d.server_time));

  on('match.found', ({ session }) => {
    qc.setQueryData(qk.session(session.id), session);
    void qc.invalidateQueries({ queryKey: qk.invites });
    cb.onMatchFound?.(session);
  });

  on('session.updated', ({ session }) => {
    qc.setQueryData<Session>(qk.session(session.id), session);
  });

  on('game.state', ({ game }) => {
    if (game.server_time) serverClock.sample(game.server_time);
    qc.setQueryData<GameView>(qk.game(game.id), (prev) => newerGame(prev, normalizeGame(game)));
  });

  // v6: Date Night state. The §7 payload has no session id; when the server adds one we file it
  // here, otherwise the call screen (the only consumer) folds it into its own session (features/date/useDate).
  on('date.state', (d) => {
    const date = d?.date;
    if (!date) return;
    if (date.server_time) serverClock.sample(date.server_time);
    const sid = d.session_id ?? date.session_id;
    if (sid) qc.setQueryData<DateView | null>(qk.date(sid), (prev) => newerDate(prev, normalizeDate(date)));
  });

  // v6.1 Popin Dates: keep the list and the single invite (lobby) in sync.
  on('date_invite.updated', ({ invite, reminder }) => {
    if (!invite) return;
    if (invite.server_time) serverClock.sample(invite.server_time);
    qc.setQueryData<DateInvite>(qk.dateInvite(invite.id), (prev) => ({ ...invite, url: prev?.url ?? invite.url }));
    qc.setQueryData<{ invites: DateInvite[] }>(qk.dateInvites, (prev) =>
      prev ? { invites: prev.invites.some((x) => x.id === invite.id) ? prev.invites.map((x) => (x.id === invite.id ? invite : x)) : [invite, ...prev.invites] } : prev,
    );
    if (reminder) cb.onDateReminder?.(invite);
  });

  on('game.effect', ({ game_id, kind, data }) => {
    qc.setQueryData<GameView | undefined>(qk.game(game_id), (prev) => applyEffect(prev, kind, data));
  });

  const upsertInvite = (invite: Invite) => {
    const myId = useAuth.getState().me?.id;
    const dir: 'incoming' | 'outgoing' = myId && invite.from.id === myId ? 'outgoing' : 'incoming';
    qc.setQueryData<{ incoming: Invite[]; outgoing: Invite[] }>(qk.invites, (prev) => {
      if (!prev) return prev;
      const merge = (list: Invite[]) => {
        const rest = list.filter((x) => x.id !== invite.id);
        return invite.status === 'pending' ? [invite, ...rest] : rest;
      };
      return dir === 'incoming' ? { ...prev, incoming: merge(prev.incoming) } : { ...prev, outgoing: merge(prev.outgoing) };
    });
    void qc.invalidateQueries({ queryKey: qk.invites });
  };

  on('invite.received', ({ invite }) => {
    upsertInvite(invite);
    cb.onInviteReceived?.(invite);
  });
  on('invite.updated', ({ invite }) => upsertInvite(invite));

  on('connection.created', ({ connection }) => {
    useRealtime.getState().markConnected(connection.user.id);
    qc.setQueryData<{ connections: Connection[] }>(qk.connections, (prev) =>
      prev ? { connections: [connection, ...prev.connections.filter((c) => c.id !== connection.id)] } : prev,
    );
    void qc.invalidateQueries({ queryKey: qk.connections });
    cb.onConnectionCreated?.(connection);
  });

  on('pod.updated', ({ pod }) => {
    qc.setQueryData<Pod>(qk.pod(pod.id), pod);
  });

  // ---- v2: plan, friends, chat
  on('plan.updated', (d) => {
    const me = useAuth.getState().me;
    if (me) {
      // v5: a running rewarded filter pass keeps filters on; Plus (incl. a day pass) is ad-free.
      const pass = !!me.filter_pass_until && Date.parse(me.filter_pass_until) > Date.now();
      const has = (me.match_filters?.genders.length ?? 0) > 0 || (me.match_filters?.countries.length ?? 0) > 0;
      useAuth.getState().setMe({ ...me, plan: d.plan, plus_until: d.plus_until ?? null, filters_active: (d.plan === 'plus' || pass) && has, ads_eligible: d.plan === 'plus' ? false : me.ads_eligible });
    }
    endpoints
      .me()
      .then((fresh) => useAuth.getState().setMe(fresh))
      .catch(() => undefined);
    void qc.invalidateQueries({ queryKey: qk.subscription });
    cb.onPlanUpdated?.(d.plan);
  });

  const upsertRequest = (r: FriendRequest) => {
    const myId = useAuth.getState().me?.id;
    const dir: keyof Requests = myId && r.from.id === myId ? 'outgoing' : 'incoming';
    qc.setQueryData<Requests>(qk.friendRequests, (prev) => {
      if (!prev) return prev;
      const rest = prev[dir].filter((x) => x.id !== r.id);
      return { ...prev, [dir]: r.status === 'pending' ? [r, ...rest] : rest };
    });
    void qc.invalidateQueries({ queryKey: qk.recentPeople });
    if (r.status === 'accepted') {
      void qc.invalidateQueries({ queryKey: qk.connections });
      void qc.invalidateQueries({ queryKey: qk.chats });
    }
  };
  on('friend_request.received', ({ request }) => {
    upsertRequest(request);
    void qc.invalidateQueries({ queryKey: qk.friendRequests });
    cb.onFriendRequest?.(request);
  });
  on('friend_request.updated', ({ request }) => upsertRequest(request));

  on('connection.removed', ({ connection_id }) => {
    qc.setQueryData<{ connections: Connection[] }>(qk.connections, (prev) => (prev ? { connections: prev.connections.filter((c) => c.id !== connection_id) } : prev));
    qc.setQueryData<Chats>(qk.chats, (prev) => (prev ? { chats: prev.chats.filter((c) => c.connection_id !== connection_id) } : prev));
    useChat.getState().hide(connection_id);
    void qc.invalidateQueries({ queryKey: qk.recentPeople });
    void qc.invalidateQueries({ queryKey: qk.hangouts });
  });

  on('chat.message', ({ message: raw }) => {
    const message = normalizeMessage(raw);
    const chat = useChat.getState();
    const myId = useAuth.getState().me?.id;
    chat.receive(message);
    const prev = qc.getQueryData<Chats>(qk.chats);
    qc.setQueryData<Chats>(qk.chats, (p) => applyChatMessage(p, message, myId, chat.active));
    if (!prev?.chats.some((c) => c.connection_id === message.connection_id)) void qc.invalidateQueries({ queryKey: qk.chats });
    if (message.sender_id !== myId && chat.active !== message.connection_id) {
      const friend = prev?.chats.find((c) => c.connection_id === message.connection_id)?.friend.display_name ?? null;
      cb.onChatMessage?.(message, friend);
    }
  });
  // v3: a challenge changed (guess, solve, miss, expire). Fold it into the thread + list preview.
  on('chat.challenge_updated', ({ message: raw }) => {
    const message = normalizeMessage(raw);
    useChat.getState().receive(message);
    qc.setQueryData<Chats>(qk.chats, (p) =>
      p ? { chats: p.chats.map((c) => (c.last_message?.id === message.id ? { ...c, last_message: message } : c)) } : p,
    );
  });

  // v3: proposals keep the session's pending_proposal in sync (the call screen renders from it).
  const foldProposal = ({ proposal }: { proposal: Proposal }) => {
    qc.setQueryData<Session>(qk.session(proposal.session_id), (s) => {
      if (!s) return s;
      const cur = s.pending_proposal ?? null;
      if (proposal.status === 'pending') return { ...s, pending_proposal: proposal };
      if (cur && cur.id === proposal.id) return { ...s, pending_proposal: null };
      return s;
    });
  };
  on('game.proposed', foldProposal);
  on('game.proposal_updated', foldProposal);

  on('chat.deleted', ({ message }) => {
    useChat.getState().receive({ ...message, deleted: true, body: '' });
    qc.setQueryData<Chats>(qk.chats, (p) =>
      p ? { chats: p.chats.map((c) => (c.last_message?.id === message.id ? { ...c, last_message: { ...message, deleted: true, body: '' } } : c)) } : p,
    );
  });
  on('chat.read', ({ connection_id, user_id, up_to_id }) => {
    const myId = useAuth.getState().me?.id;
    if (user_id === myId) {
      // Read on another device of mine.
      qc.setQueryData<Chats>(qk.chats, (p) => (p ? { chats: p.chats.map((c) => (c.connection_id === connection_id ? { ...c, unread_count: 0 } : c)) } : p));
    } else useChat.getState().peerRead(connection_id, up_to_id);
  });
  on('chat.typing', ({ connection_id, user_id }) => {
    if (user_id === useAuth.getState().me?.id) return;
    useChat.getState().typing(connection_id, Date.now() + TYPING_SHOW_MS);
  });

  // v8: the wallet balance changed (gift, refund, daily claim, ad, admin).
  on('wallet.updated', (d) => {
    qc.setQueryData<Wallet>(qk.wallet, (prev) => (prev ? { ...prev, balance: d.balance, locked: d.locked } : prev));
    void qc.invalidateQueries({ queryKey: qk.wallet });
    void qc.invalidateQueries({ queryKey: qk.walletHistory });
  });

  on('moderation.action', (d) => {
    const notice = { action: d.action, reason: d.reason, until: d.until ?? null };
    if (d.action === 'warn') useModeration.getState().setWarning(notice);
    else useModeration.getState().setBlocked(notice);
  });

  // v15 Us (SPEC_V15 §6).
  offs.push(...wireUs(ws, qc));

  return () => offs.forEach((off) => off());
}

/** Subscribe to one server event type for the lifetime of a component. */
export function useWsEvent<K extends ServerEventType>(type: K, handler: (d: ServerEvents[K]) => void): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => realtime.on(type, (d) => ref.current(d)), [type]);
}

export { WsClient };
